/**
 * Global Chart.js Configuration
 *
 * This file registers all Chart.js components and plugins globally at the app entry point.
 * This ensures plugin lifecycle consistency and prevents errors when switching between dashboards.
 *
 * IMPORTANT:
 * - ALL Chart.js plugins should be registered here, not in individual components
 * - Components that don't want a plugin should disable it via options
 * - Example: plugins: { datalabels: { display: false } }
 *
 * @see https://www.chartjs.org/docs/latest/developers/plugins.html
 */

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  BarController,
  LineController,
  ScatterController,
  BubbleController,
  RadialLinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import CrosshairPlugin from 'chartjs-plugin-crosshair';

/**
 * Patch the CrosshairPlugin to handle cases where options are undefined or disabled.
 * This prevents "Cannot read properties of undefined (reading 'enabled')" errors
 * on chart types that don't support crosshair (e.g., doughnut, pie charts).
 *
 * CRITICAL FIX (2025-12-17):
 * - Added afterDestroy hook to prevent "Cannot read properties of undefined (reading 'syncEventHandler')" error
 * - This error occurs when destroying charts that never initialized crosshair (pie, bar charts in Traffic Analytics)
 */
const patchedCrosshairPlugin = {
  ...CrosshairPlugin,
  afterDraw: function(chart, args, options) {
    // Skip if options are undefined, false, or chart type doesn't support crosshair
    if (!options || options === false || options.enabled === false) return;
    if (['doughnut', 'pie', 'polarArea', 'radar'].includes(chart.config.type)) return;
    if (CrosshairPlugin.afterDraw) {
      CrosshairPlugin.afterDraw.call(this, chart, args, options);
    }
  },
  afterEvent: function(chart, args, options) {
    // Skip if options are undefined, false, or chart type doesn't support crosshair
    if (!options || options === false || options.enabled === false) return;
    if (['doughnut', 'pie', 'polarArea', 'radar'].includes(chart.config.type)) return;
    if (CrosshairPlugin.afterEvent) {
      CrosshairPlugin.afterEvent.call(this, chart, args, options);
    }
  },
  beforeTooltipDraw: function(chart, args, options) {
    // Skip if options are undefined, false, or chart type doesn't support crosshair
    if (!options || options === false || options.enabled === false) return;
    if (['doughnut', 'pie', 'polarArea', 'radar'].includes(chart.config.type)) return;
    if (CrosshairPlugin.beforeTooltipDraw) {
      CrosshairPlugin.beforeTooltipDraw.call(this, chart, args, options);
    }
  },
  afterDestroy: function(chart, args, options) {
    // CRITICAL FIX: Check if chart.crosshair exists before accessing syncEventHandler
    // This prevents errors when destroying charts that never initialized crosshair
    if (!chart.crosshair || !chart.crosshair.syncEventHandler) return;

    // Skip if options are undefined, false, or chart type doesn't support crosshair
    if (!options || options === false || options.enabled === false) return;
    if (['doughnut', 'pie', 'polarArea', 'radar'].includes(chart.config.type)) return;

    if (CrosshairPlugin.afterDestroy) {
      CrosshairPlugin.afterDestroy.call(this, chart, args, options);
    }
  },
};

// Register all Chart.js components and plugins globally
ChartJS.register(
  // Scales
  CategoryScale,
  LinearScale,
  TimeScale,
  RadialLinearScale,

  // Elements
  PointElement,
  LineElement,
  BarElement,
  ArcElement,

  // Controllers
  BarController,
  LineController,
  ScatterController,
  BubbleController,

  // Plugins
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
  annotationPlugin,
  patchedCrosshairPlugin
);

// Set global defaults (optional)
ChartJS.defaults.responsive = true;
ChartJS.defaults.maintainAspectRatio = false;

// Global plugin defaults
ChartJS.defaults.plugins.datalabels = {
  display: false, // Disabled by default, components must explicitly enable
};

ChartJS.defaults.plugins.crosshair = {
  enabled: false, // Disabled by default, components must explicitly enable
};

export default ChartJS;
